'use client';

import { c as _c } from "react/compiler-runtime";
import type { Address, ChainId } from '@morpho-org/blue-sdk';
import type { Time } from '@morpho-org/morpho-ts';
import { Select } from '@morpho-org/ui-v6/client';
import SimpleChart from '@/components/charts/components/SimpleChart';
import { createPlotlineLabelFormatter } from '@/components/charts/helpers/highcharts';
import { useHighchartsOptions } from '@/components/charts/hooks/useHighchartsOptions';
import { getTimeFrameOptionsForVaultAge } from '@/config/table';
import type { VaultV2ApyOverviewData } from '../VaultV2ApyOverview.mapper';
import { useVaultV2ApyChartData } from '../hooks/useVaultV2ApyChartData';
export interface VaultV2ChartApyCardProps {
  apyOverview: VaultV2ApyOverviewData;
  now: number;
  vaultAddress: Address;
  chainId: ChainId;
}

/**
 * Chart component to display the vault APY over time.
 * Shows a time series chart with average APY and net APY metrics.
 */
const VaultV2ChartApyCard = t0 => {
  const $ = _c(41);
  const {
    apyOverview,
    now,
    vaultAddress,
    chainId
  } = t0;
  const {
    palette
  } = useHighchartsOptions();
  let t1;
  if ($[0] !== apyOverview.creationTimestamp || $[1] !== now) {
    t1 = getTimeFrameOptionsForVaultAge(apyOverview.creationTimestamp, now);
    $[0] = apyOverview.creationTimestamp;
    $[1] = now;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const timeFrameOptions = t1;
  let t2;
  if ($[3] !== apyOverview || $[4] !== chainId || $[5] !== vaultAddress) {
    t2 = {
      apyOverview,
      vaultAddress,
      chainId
    };
    $[3] = apyOverview;
    $[4] = chainId;
    $[5] = vaultAddress;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const {
    timeFrame,
    setTimeFrame,
    formattedData,
    formattedPeriodAvgNetApy,
    formattedNetApy,
    netApy,
    loading,
    error
  } = useVaultV2ApyChartData(t2);
  let t3;
  if ($[7] !== formattedData || $[8] !== palette.areaChartColors[0] || $[9] !== palette.colors[0]) {
    t3 = [{
      type: "areaspline" as const,
      name: "APY",
      data: formattedData,
      color: palette.colors[0],
      lineColor: palette.colors[0],
      fillColor: palette.areaChartColors[0]
    }];
    $[7] = formattedData;
    $[8] = palette.areaChartColors[0];
    $[9] = palette.colors[0];
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const series = t3;
  const t4 = netApy ?? undefined;
  const t5 = `Avg ${formattedPeriodAvgNetApy}%`;
  let t6;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = createPlotlineLabelFormatter("var(--foreground)");
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t5) {
    t7 = {
      x: 6,
      y: -15,
      text: t5,
      align: "left" as const,
      useHTML: true,
      formatter: t6
    };
    $[12] = t5;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== t4 || $[15] !== t7) {
    t8 = [{
      value: t4,
      className: "hc-plotline-brand",
      color: "var(--primary)",
      dashStyle: "Dot" as const,
      width: 1,
      label: t7
    }];
    $[14] = t4;
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const plotlinesY = t8;
  let t9;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      yAxis: {
        softMin: 0,
        maxPadding: 0.4
      },
      chart: {
        plotBackgroundColor: "var(--muted)"
      }
    };
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  const additionalOptions = t9;
  let t10;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <span className="text-secondary-foreground text-xs" style={{
      lineHeight: "normal"
    }}>Net APY</span>;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== formattedNetApy) {
    t11 = <span className="text-title-sm text-foreground">{formattedNetApy}</span>;
    $[19] = formattedNetApy;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <span className="text-title-sm text-muted-foreground">%</span>;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] !== t11) {
    t13 = <div className="gap-xs flex flex-col">{t10}<div className="gap-xs flex items-center"><div className="flex items-center gap-[2px]">{t11}{t12}</div></div></div>;
    $[22] = t11;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  let t14;
  if ($[24] !== setTimeFrame || $[25] !== timeFrame || $[26] !== timeFrameOptions) {
    t14 = <Select options={timeFrameOptions} value={timeFrame} onChange={setTimeFrame} />;
    $[24] = setTimeFrame;
    $[25] = timeFrame;
    $[26] = timeFrameOptions;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] !== t13 || $[29] !== t14) {
    t15 = <div className="flex items-center justify-between">{t13}{t14}</div>;
    $[28] = t13;
    $[29] = t14;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  let t16;
  if ($[31] !== error || $[32] !== loading) {
    t16 = {
      loading,
      error
    };
    $[31] = error;
    $[32] = loading;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  let t17;
  if ($[34] !== plotlinesY || $[35] !== series || $[36] !== t16) {
    t17 = <SimpleChart statuses={t16} unit="%" height="225px" series={series} plotlinesY={plotlinesY} additionalOptions={additionalOptions} />;
    $[34] = plotlinesY;
    $[35] = series;
    $[36] = t16;
    $[37] = t17;
  } else {
    t17 = $[37];
  }
  let t18;
  if ($[38] !== t15 || $[39] !== t17) {
    t18 = <div data-testid="vault-v2-apy-chart" className="gap-lg flex min-w-[220px] grow flex-col">{t15}{t17}</div>;
    $[38] = t15;
    $[39] = t17;
    $[40] = t18;
  } else {
    t18 = $[40];
  }
  return t18;
};
export default VaultV2ChartApyCard;